import React from 'react';
import { Tooltip } from '@heroui/react';
import { Typography } from '../../../../components';
import { InfoCircle } from '../../../../components/icons';
import { RefShareOffer } from '../../../../api/services';
import { formatTimeAgo } from '../../../../utils/formatTimeAgo';
import { useTranslations } from '../../../../contexts/translation.context';
import { Icon } from '@iconify/react';

interface StatisticsCellProps {
  offer: RefShareOffer;
}

interface ArpuTooltipContentProps {
  arpu7days: number;
  arpu30days: number;
}

const ArpuTooltipContent: React.FC<ArpuTooltipContentProps> = ({ arpu7days, arpu30days }) => {
  return (
    <div className="bg-white border border-[#EDEDED] rounded-[12px] shadow-[0px_0px_11px_0px_rgba(0,0,0,0.1)] p-[16px] min-w-[200px]">
      <div className="mb-[8px]">
        <Typography variant="body2" className="text-[#11181c]">
          Средний доход с пользователя
        </Typography>
      </div>
      <div className="flex flex-col gap-[4px]">
        <div className="flex items-center justify-between py-[6px]">
          <Typography variant="label3" className="text-[#888b8d]">
            7 дней
          </Typography>
          <Typography variant="label3" className="text-zinc-700 font-medium">
            ${typeof arpu7days === 'number' ? arpu7days.toFixed(2) : arpu7days}
          </Typography>
        </div>
        <div className="flex items-center justify-between py-[6px]">
          <Typography variant="label3" className="text-[#888b8d]">
            30 дней
          </Typography>
          <Typography variant="label3" className="text-zinc-700 font-medium">
            ${typeof arpu30days === 'number' ? arpu30days.toFixed(2) : arpu30days}
          </Typography>
        </div>
      </div>
    </div>
  );
};

const StatisticsCell: React.FC<StatisticsCellProps> = ({ offer }) => {
  const { t } = useTranslations();

  const arpu = offer.ref_share_transactions_per_subscriber ?? 0;
  const arpu7days = (offer as any).ref_share_transactions_per_subscriber_7days ?? arpu;
  const arpu30days = (offer as any).ref_share_transactions_per_subscriber_30days ?? arpu;
  const activeCampaigns = offer.ref_share_active_total ?? 0;
  const createdAt = offer.created_at || new Date().toISOString();

  return (
    <div className="flex flex-col gap-[4px]">
      <div className="flex items-center gap-[4px]">
        <Typography variant="body3" className="text-zinc-500 text-[12px] leading-[16px]">
          ARPU:
        </Typography>
        <Typography variant="body3" className="text-[#11181c] font-semibold text-[14px] leading-[16px]">
          ${typeof arpu === 'number' ? arpu.toFixed(2) : arpu}
        </Typography>
        <Tooltip
          content={<ArpuTooltipContent arpu7days={arpu7days} arpu30days={arpu30days} />}
          placement="bottom"
          showArrow={false}
          classNames={{
            content: 'p-0 bg-transparent shadow-lg',
          }}
        >
          <span className="cursor-pointer flex items-center">
            <InfoCircle size={14} />
          </span>
        </Tooltip>
      </div>

      <div className="flex items-center gap-[4px]">
        <Typography variant="body3" className="text-zinc-500 text-[12px] leading-[16px]">
          Активные кампании:
        </Typography>
        <Typography variant="body3" className="text-[#11181c] font-semibold text-[14px] leading-[16px]">
          {activeCampaigns}
        </Typography>
      </div>

      <div className="flex items-center gap-[4px]">
        <Icon icon="solar:calendar-linear" className="w-[14px] h-[14px] text-zinc-500" />
        <Typography variant="body3" className="text-zinc-500 text-[12px] leading-[16px]">
          {formatTimeAgo(createdAt, t)}
        </Typography>
      </div>
    </div>
  );
};

export default StatisticsCell;
